import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, Typography, AppBar, Toolbar, Drawer, Button, Avatar, IconButton, useMediaQuery, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import MenuComponent from './Menu';
import { useTheme } from '@mui/material/styles';
import './Layout.css';

const drawerWidth = 260;

const Layout = ({ children }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [authorized, setAuthorized] = useState(true);
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        const firstName = localStorage.getItem('first_name');
        const lastName = localStorage.getItem('last_name');
        const authToken = localStorage.getItem('token');

        if (firstName && lastName && authToken) {
            setFirstName(firstName);
            setLastName(lastName);
        } else {
            setAuthorized(false);
        }
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        navigate('/logout');
    };

    if (!authorized) {
        return (
            <Box className="layout-root" sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ p: 4, textAlign: 'center', background: 'white', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                    <Typography variant="h6" color="error" sx={{ mb: 2, fontWeight: 600 }}>
                        Sessione scaduta o non autorizzata.
                    </Typography>
                    <Button variant="contained" color="primary" onClick={() => navigate('/')} sx={{ borderRadius: 2 }}>
                        Torna al Login
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box className="layout-root">
            <CssBaseline />
            <AppBar
                position="fixed"
                className="glass-appbar"
                elevation={0}
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` }
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Avatar
                        alt="Logo"
                        src="img/corso-di-lingue-per-bambini.png.webp"
                        sx={{ width: 38, height: 38, marginRight: 1.5 }}
                    />
                    <Typography variant="h6" noWrap component="div" className="nav-title">
                        {isSmallScreen ? 'H&L App' : 'Hocus&Lotus App'}
                    </Typography>

                    <Box className="user-profile-section" onClick={handleMenuOpen} sx={{ cursor: 'pointer' }}>
                        <Avatar
                            alt="User Profile"
                            src="img/insegnare-ai-bambini.png.webp"
                            sx={{ width: 32, height: 32, mr: 1.5, border: '1px solid rgba(0,0,0,0.05)' }}
                        />
                        {!isSmallScreen && (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#444', mr: 1 }}>
                                {firstName} {lastName}
                            </Typography>
                        )}
                    </Box>

                    <Menu
                        id="user-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{
                            sx: {
                                mt: 1.5,
                                borderRadius: 3,
                                minWidth: 180,
                                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                border: '1px solid rgba(0,0,0,0.05)'
                            }
                        }}
                    >
                        {isSmallScreen && (
                            <MenuItem disabled>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#333' }}>
                                    {firstName} {lastName}
                                </Typography>
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleLogout} sx={{ color: 'error.main', py: 1.5 }}>
                            <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Logout</Typography>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    className="glass-drawer"
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { width: drawerWidth },
                    }}
                >
                    <MenuComponent />
                </Drawer>
                <Drawer
                    variant="permanent"
                    className="glass-drawer"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { width: drawerWidth },
                    }}
                    open
                >
                    <MenuComponent />
                </Drawer>
            </Box>

            <Box
                component="main"
                className="main-content"
                sx={{
                    flexGrow: 1,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    minWidth: 0
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default Layout;
